import React, { useState, useEffect } from "react";
import { Box, Chip, Typography } from "@mui/material";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import OpenInNewTab from "components/Shared/OpenInNewTab";
import FileIcon from "components/Shared/FileIcon";
import { useDeliveryDetailContext } from "contexts/DeliveryDetailContext";
import { useAuth } from "hooks";
import { localizeDateTimeShort } from "utils/dateFormats";
import { reverseGeocode } from "utils/reverseGeocode";

const statusStyles = {
  Pending: { backgroundColor: "#FADBD8", textColor: "#C0392B" },
  Confirmed: { backgroundColor: "#FFF3CD", textColor: "#856404" },
  Completed: { backgroundColor: "#D4EDDA", textColor: "#155724" },
  default: { backgroundColor: "#E9ECEF", textColor: "#6C757D" },
};
const getStatusStyle = (status) => statusStyles[status] || statusStyles.default;

const Row = ({ label, children, value, fullSpan = false }) => (
  <Box sx={{
    display: "flex", py: 0.6, px: 1.5, gap: 1, borderRadius: 1,
    transition: "background-color 0.15s ease",
    "&:hover": { bgcolor: "action.hover" },
    ...(fullSpan ? { gridColumn: "1 / -1" } : {}),
  }}>
    <Box sx={{ width: "45%", color: "text.primary", fontSize: "0.66rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, flexShrink: 0 }}>
      {label}
    </Box>
    <Box sx={{ width: "55%", fontSize: "0.78rem", fontWeight: 600, wordBreak: "break-word" }}>
      {children ? children : value ?? "N/A"}
    </Box>
  </Box>
);

const DeliveryBasicDetailCompact = () => {
  const { isCustomer, isVendor, isContractor, isDriver } = useAuth();
  const canGoToLink = isCustomer || isVendor || isContractor || isDriver;
  const { deliveryDetail, loadingDelivery } = useDeliveryDetailContext();
  const [locationName, setLocationName] = useState("Fetching location...");

  useEffect(() => {
    const fetchLocationName = async () => {
      if (
        deliveryDetail?.deliveryLocation?.lat &&
        deliveryDetail?.deliveryLocation?.lon
      ) {
        const name = await reverseGeocode(
          deliveryDetail.deliveryLocation.lat,
          deliveryDetail.deliveryLocation.lon
        );
        setLocationName(name);
      } else {
        setLocationName("Location not available");
      }
    };

    fetchLocationName();
  }, [deliveryDetail?.deliveryLocation]);

  if (loadingDelivery) return <InfoGridSkeleton />;

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
      <Row label="Delivery ID" value={deliveryDetail?.fulfillmentCompletionId} />
      <Row label={deliveryDetail?.sendOutId ? "SendOut" : "Delivered By"}>
        {deliveryDetail?.sendOutId
          ? typeof deliveryDetail.sendOutId === "object"
            ? canGoToLink
              ? deliveryDetail.sendOutId.sendOutId || "N/A"
              : (
                <OpenInNewTab
                  label={deliveryDetail.sendOutId.sendOutId || "N/A"}
                  link={`/app/crm/sendOut/${deliveryDetail.sendOutId._id}/detail`}
                />
              )
            : deliveryDetail.sendOutId
          : deliveryDetail?.responsibleUser?.name}
      </Row>
      <Row
        label="Delivery Time"
        value={deliveryDetail?.actualDeliveryTime ? localizeDateTimeShort(deliveryDetail.actualDeliveryTime) : "N/A"}
      />
      <Row label="Created By" value={deliveryDetail?.createdBy?.name} />
      <Row label="Created At" value={deliveryDetail?.createdAt ? localizeDateTimeShort(deliveryDetail.createdAt) : "N/A"} />
      <Row label="Status">
        <Chip
          label={deliveryDetail?.status || "Unknown"}
          size="small"
          sx={{
            backgroundColor: getStatusStyle(deliveryDetail?.status).backgroundColor,
            color: getStatusStyle(deliveryDetail?.status).textColor,
            height: 18, fontSize: "0.6rem", fontWeight: "bold",
          }}
        />
      </Row>
      <Row label="Location">
        <Box>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 600 }}>{locationName}</Typography>
          {deliveryDetail?.deliveryLocation?.lat && deliveryDetail?.deliveryLocation?.lon && (
            <Typography sx={{ fontSize: "0.66rem", color: "text.secondary" }}>
              ({deliveryDetail.deliveryLocation.lat.toFixed(6)}, {deliveryDetail.deliveryLocation.lon.toFixed(6)})
            </Typography>
          )}
        </Box>
      </Row>
      {deliveryDetail?.attachments?.length > 0 && (
        <Row label="Attachments">
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {deliveryDetail.attachments.map((file) => (
              <a key={file?._id} href={file?.url} target="_blank" rel="noreferrer" style={{ color: "var(--primary)" }}>
                <FileIcon fileName={file?.name} fileUrl={file?.url} />
              </a>
            ))}
          </Box>
        </Row>
      )}
      {deliveryDetail?.receiveInBehalf && (
        <>
          <Row label="Receive By" value={deliveryDetail?.receiveBy} />
          <Row label="Receiver Phone" value={deliveryDetail?.receiverPhone} />
        </>
      )}
      <Row label="Signature" fullSpan>
        {deliveryDetail?.signature ? (
          <Box
            component="img"
            src={deliveryDetail.signature}
            alt="Delivery Signature"
            sx={{
              maxWidth: 1, height: "auto", maxHeight: 120,
              border: "1px solid", borderColor: "divider",
              bgcolor: "#f3f4f6", borderRadius: 1, p: 0.5,
            }}
          />
        ) : (
          "No signature available"
        )}
      </Row>
    </Box>
  );
};

export default DeliveryBasicDetailCompact;
